import { DI } from '@microsoft/fast-foundation';
import { Auth, Connect, Session } from '@genesislcap/foundation-comms';
import { PROFILE_RIGHTS, logger } from '../utils';
import { IUserService } from './user.service';
import { MessageBuilder } from '@genesislcap/foundation-comms';
import { AuthError } from '@genesislcap/foundation-comms';

export interface IAuthService {
  logout();
  setLastInteractionTime();
  getLastInteractionTime(): number;
  logoutOnSessionIsNotValid();
  validateProfile(profileRight: PROFILE_RIGHTS): boolean;
  defaultIdletTimeout: number;
  defaultIdletTimeCheck: number;
}

class AuthServiceImpl implements IAuthService {
  constructor(
    @Connect private connect: Connect,
    @Auth private auth: Auth,
    @IUserService private userService: IUserService,
    @Session private session: Session,
    @MessageBuilder private messageBuilder: MessageBuilder,
  ) {}

  private lastInteractionTime = new Date().getTime();

  public defaultIdletTimeout: number = 1_800_000;
  public defaultIdletTimeCheck: number = 60_000;

  setLastInteractionTime() {
    this.lastInteractionTime = new Date().getTime();
  }
  getLastInteractionTime() {
    return this.lastInteractionTime;
  }

  public async logoutOnSessionIsNotValid() {
    this.auth.isWorking = false;
    await this.connect.setValidSession(false);
    await this.session.deleteUserFromSessionStorage();
    location.reload();
  }

  public async logoutCalledEvent(authToken: string): Promise<any> {
    const request: any = await this.connect
      .commitEvent('EVENT_CUSTOM_AUTH_LOGOUT_OIDC', {
        DETAILS: { AUTH_TOKEN: authToken },
      })
      .catch((error) => {
        logger.error(error);
      });
    return request;
  }

  async logout() {
    var _a, _b;
    let logoutResult = {
      success: false,
    };
    try {
      const authToken = this.session.getSessionStorageItem('authToken');
      if (authToken) {
        await this.logoutCalledEvent(authToken);
      }
      const logoutMessage = this.messageBuilder.createLogoutMessage(this.auth.loggedUserResult);
      logger.warn(logoutMessage);
      const { CODE, DETAILS, ERROR, MESSAGE_TYPE, OIDC_LOGOUT_URL } =
        await this.connect.send(logoutMessage);
      const error =
        (_a = DETAILS === null || DETAILS === void 0 ? void 0 : DETAILS.ERROR) !== null &&
        _a !== void 0
          ? _a
          : ERROR;

      if (OIDC_LOGOUT_URL) {
        const oidcUrl = new URL(OIDC_LOGOUT_URL);
        const urlPath = `${oidcUrl.origin}${oidcUrl.pathname}`;
        const params = `?id_token_hint=${authToken}&client_id=${oidcUrl.searchParams.get('client_id')}`;
        // Set to work on all browsers if we need to follow redirect
        sessionStorage.clear();
        setTimeout(function () {
          document.location.href = `${urlPath}${params}`;
        });
      }
    } catch (err) {
      const logoutError = new AuthError(err.sentMessage, err.receivedMessage);
      logger.error('Logout Authentication Error:', logoutError);
      if (logoutError.isIgnorableError()) {
        logoutResult.success = true;
      } else {
        throw err;
      }
    } finally {
      this.auth.isWorking = false;
      await this.connect.setValidSession(false);
      await this.session.deleteUserFromSessionStorage();
    }
    location.reload();
  }

  validateProfile(profileRight: PROFILE_RIGHTS): boolean {
    return this.userService.getFilteredPermissions().includes(profileRight);
  }
}

export const IAuthService = DI.createInterface<IAuthService>((x) => x.singleton(AuthServiceImpl));
